<template>
  <div class="plan-send-status">
    <a-spin
      v-if="loading"
      size="small" />

    <a-tooltip title="Обновить статус отправки">
      <a-button
        v-if="!loading"
        size="small"
        :loading="refreshing"
        :disabled="sending"
        aria-label="Обновить статус отправки"
        @click="refreshStatus">
        <template #icon>
          <reload-outlined />
        </template>
      </a-button>
    </a-tooltip>

    <span
      v-if="loadError"
      class="plan-send-status__error">
      Не удалось обновить статус отправки
    </span>

    <template v-if="!loading && status">
      <a-tag :color="tagColor">
        {{ tagText }}
      </a-tag>

      <a-popover
        placement="bottomLeft"
        overlay-class-name="plan-send-status__popover">
        <template #title>
          {{ popoverTitle }}
        </template>

        <template #content>
          <p
            v-if="sendNote"
            class="plan-send-status__note">
            {{ sendNote }}
          </p>

          <ul class="plan-send-status__conditions">
            <li
              v-for="condition in status.conditions"
              :key="condition.key">
              <check-circle-outlined
                v-if="condition.passed"
                class="plan-send-status__ok" />
              <close-circle-outlined
                v-else
                class="plan-send-status__fail" />
              <span>{{ condition.label }}</span>
              <span
                v-if="conditionDetail(condition)"
                class="plan-send-status__detail">
                — {{ conditionDetail(condition) }}
              </span>
            </li>
          </ul>

          <template v-if="status.webhooks.length">
            <div class="plan-send-status__section">
              Вебхуки заказчика
            </div>
            <ul class="plan-send-status__conditions">
              <li
                v-for="webhook in status.webhooks"
                :key="webhook.id">
                <check-circle-outlined
                  v-if="webhook.sent"
                  class="plan-send-status__ok" />
                <close-circle-outlined
                  v-else
                  class="plan-send-status__fail" />
                <span>{{ webhook.url }}</span>
                <span class="plan-send-status__detail">
                  — {{ webhookDetail(webhook) }}
                </span>
              </li>
            </ul>
          </template>
        </template>

        <info-circle-outlined class="plan-send-status__tip" />
      </a-popover>

      <a-tooltip :title="blockersHint">
        <a-button
          v-if="canSendToCustomer"
          size="small"
          :loading="sending"
          :disabled="!status.can_send"
          @click="confirmSend">
          Отправить
        </a-button>
      </a-tooltip>

      <div
        v-if="status.blockers?.length"
        class="plan-send-status__current">
        <span>Сейчас отправка недоступна:</span>
        <ul>
          <li
            v-for="blocker in status.blockers"
            :key="blocker">
            {{ blocker }}
          </li>
        </ul>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { Modal, message as antMessage } from 'ant-design-vue';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons-vue';
import { api } from '@/api';
import { usePlanSendStatus } from '@/composables/usePlanSendStatus';
import {
  conditionDetail,
  conditionsTitle,
  formatSendDateTime,
  isSentState,
  lastSendNote,
  sendStateColor,
  sendStateText,
  webhookDetail,
} from '@/helpers/planSendStatus';

const props = defineProps({
  planId: {
    type: [Number, String],
    required: true,
  },
  revision: {
    type: String,
    default: '',
  },
});

const store = useStore();
const canSendToCustomer = computed(() => store.getters['auth/hasPermission']('api.crm.plans.send_to_customer'));
const {
  status, loading, refreshing, loadError, refreshStatus, setStatus,
} = usePlanSendStatus(() => props.planId, () => props.revision);
const sending = ref(false);

const isSent = computed(() => isSentState(status.value?.state));

const tagColor = computed(() => sendStateColor(status.value?.state));

const tagText = computed(() => sendStateText(status.value));

const popoverTitle = computed(() => conditionsTitle(status.value));

const blockersHint = computed(() => (status.value?.can_send
  ? ''
  : `Отправить нельзя: ${(status.value?.blockers ?? []).join('; ')}`));

const sendNote = computed(() => lastSendNote(status.value));

const send = async () => {
  sending.value = true;
  try {
    const response = await api.sendCrmPlanToCustomer(props.planId);
    setStatus(response.data.status);
    antMessage.success(response.data.message);
  } catch (error) {
    const failedOutcome = error.response?.data;
    if (failedOutcome?.status) setStatus(failedOutcome.status);
    else await refreshStatus();
  } finally {
    sending.value = false;
  }
};

const confirmSend = () => {
  if (!isSent.value) {
    send();
    return;
  }

  Modal.confirm({
    title: 'Отправить повторно?',
    content: `Планировка уже уезжала заказчику ${formatSendDateTime(status.value.last_send?.sent_at)}. `
      + 'Заказчик получит её ещё раз.',
    okText: 'Отправить',
    cancelText: 'Отмена',
    onOk: send,
  });
};

</script>

<style scoped>
.plan-send-status {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: .5rem;
}

.plan-send-status__tip {
  color: #8a8a8a;
  cursor: help;
}

.plan-send-status__note {
  max-width: 520px;
  margin: 0 0 .5rem;
  color: #595959;
}

.plan-send-status__section {
  margin: .75rem 0 .25rem;
  font-weight: 600;
}

.plan-send-status__conditions {
  max-width: 520px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.plan-send-status__conditions li {
  display: flex;
  align-items: baseline;
  gap: .25rem;
  padding: 1px 0;
  word-break: break-word;
}

.plan-send-status__ok {
  color: #52c41a;
}

.plan-send-status__fail {
  color: #ff4d4f;
}

.plan-send-status__detail {
  color: #8a8a8a;
}
.plan-send-status__current {
  flex-basis: 100%;
  color: #595959;
}

.plan-send-status__current ul {
  margin: .25rem 0 0;
  padding-left: 1.25rem;
}

.plan-send-status__error {
  color: #ff4d4f;
}
</style>
